import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import type { PatientOut } from '../api/patients-api';
import { IconAction } from '@/components/ui/icon-action';
import { P, usePermissions } from '@/features/permissions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export type PatientSourceKind = 'uploaded' | 'generated' | 'encounter' | 'imaging';
const fields = [
  ['first_name', 'First name', 'text'], ['last_name', 'Last name', 'text'],
  ['date_of_birth', 'Date of birth', 'date'], ['phone', 'Patient phone', 'tel'],
  ['email', 'Patient email', 'email'], ['address', 'Patient address', 'text'],
  ['insurance_provider', 'Insurance provider', 'text'], ['insurance_policy_number', 'Policy / member number', 'text'],
  ['insurance_group_number', 'Insurance group number', 'text'],
] as const;
export type PatientIntakeResult = { patient: PatientOut; study_revision: number | null };
type Demographics = Record<(typeof fields)[number][0] | 'gender', string>;
type Suggestion = { demographics: Demographics; source_version: string; source_text: string; processing: boolean;
  warnings: string[]; matches: PatientOut[] };

export function CreatePatientFromSource({ kind, sourceId, onComplete, disabled = false }: {
  kind: PatientSourceKind; sourceId: string; disabled?: boolean; onComplete?: (result: PatientIntakeResult) => void;
}) {
  const { can } = usePermissions();
  const { organization } = useAuth();
  const [open, setOpen] = useState(false);
  if (!can(P.CREATE_PATIENT) || !can(P.READ_PATIENT)) return null;
  return <>
    <IconAction icon={UserPlus} variant="outline" disabled={disabled} label="Create Patient" onClick={() => setOpen(true)} />
    {open && <PatientIntakeDialog key={`${organization?.organization_id}:${kind}:${sourceId}`} kind={kind} sourceId={sourceId}
      close={() => setOpen(false)} onComplete={onComplete} />}
  </>;
}

function errorMessage(error: unknown) {
  const data = (error as { response?: { data?: { detail?: unknown; message?: string } } }).response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (data?.detail && typeof data.detail === 'object' && 'message' in data.detail) return String(data.detail.message);
  return data?.message || 'Unable to complete this step. Your entries have been kept; please try again.';
}

function PatientIntakeDialog({ kind, close, sourceId, onComplete }: {
  kind: PatientSourceKind; sourceId: string; close: () => void; onComplete?: (result: PatientIntakeResult) => void;
}) {
  const { organization } = useAuth();
  const org = organization?.organization_id;
  const base = `/api/v1/organizations/${org}/patients/intake/${kind}/${sourceId}`;
  const qc = useQueryClient();
  const id = useId();
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [values, setValues] = useState<Demographics | null>(null);
  const [mrn, setMrn] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [allowSimilar, setAllowSimilar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [manual, setManual] = useState(false);
  const edited = useRef(new Set<string>());

  useEffect(() => {
    if (manual) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;
    async function load(start: boolean) {
      try {
        const { data } = await clientApi.post<Suggestion>(`${base}/suggest?start_extraction=${start}`, {}, { timeout: 120000 });
        if (!active) return;
        setSuggestion(data);
        setValues(previous => {
          const next = { ...data.demographics };
          for (const key of edited.current) next[key as keyof Demographics] = previous?.[key as keyof Demographics] || '';
          return next;
        });
        setLoading(false);
        if (data.processing && polls++ < 40) timer = setTimeout(() => void load(false), 3000);
        else if (data.processing) setError('Document reading is taking longer than expected. You can enter details manually or retry later.');
      } catch (e) {
        if (active) { setError(errorMessage(e)); setLoading(false); }
      }
    }
    void load(true);
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [base, attempt, manual]);

  async function finish(existingPatientId?: string) {
    if (!suggestion || !values || !reviewed) return;
    setBusy(true); setError('');
    try {
      const { data } = await clientApi.post<PatientIntakeResult>(`${base}/finish`, {
        reviewed, demographics: values, medical_record_number: mrn,
        allow_similar_patient: allowSimilar, existing_patient_id: existingPatientId, source_version: suggestion.source_version,
      });
      // Creation/assignment committed successfully. Cache refresh failure must not encourage a second creation.
      for (const key of ['patients', 'encounters', 'documents', 'uploaded-documents', 'imaging']) {
        void qc.invalidateQueries({ queryKey: [key, org] });
      }
      toast.success(existingPatientId ? 'Matched to the existing patient' : 'Patient created and source linked');
      close();
      onComplete?.(data);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: { matches?: PatientOut[] } } } }).response?.data?.detail;
      if (detail?.matches) { setSuggestion({ ...suggestion, matches: detail.matches }); setAllowSimilar(false); }
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  function edit(key: keyof Demographics, value: string) {
    edited.current.add(key); setValues(previous => previous && ({ ...previous, [key]: value }));
    setReviewed(false); setAllowSimilar(false);
  }
  return <Dialog open onOpenChange={next => { if (!next && !busy) close(); }}>
    <DialogContent showCloseButton={!busy} className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle>Create Patient</DialogTitle>
        <DialogDescription>Review the extracted patient details and complete missing fields. Creating the chart also links this item.
          {kind === 'imaging' && ' Pregnancy matching is completed after the patient is created.'}</DialogDescription>
      </DialogHeader>
      {loading && <p role="status" className="flex items-center gap-2"><Loader2 className="size-4 animate-spin" />Reading patient details…</p>}
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {!loading && <Button disabled={busy} variant="ghost" onClick={() => { setError(''); setManual(false); setReviewed(false); setLoading(true); setAttempt(a => a + 1); }}>Retry extraction</Button>}
      {suggestion && values && <form className="flex flex-col gap-4" onSubmit={e => { e.preventDefault(); void finish(); }}>
        {suggestion.processing && <Button type="button" disabled={busy} variant="outline" onClick={() => { setManual(true); setLoading(false); setSuggestion({ ...suggestion, processing: false, warnings: ['Automatic reading paused here. Enter the patient details from the source.'] }); }}>Enter details manually</Button>}
        {suggestion.warnings.map(warning => <Alert key={warning}><AlertDescription>{warning}</AlertDescription></Alert>)}
        {suggestion.source_text && <details><summary className="cursor-pointer">View source text / DICOM identifiers</summary>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-sm">{suggestion.source_text}</pre></details>}
        <FieldGroup className="grid gap-4 sm:grid-cols-2">
          {fields.map(([key, label, type]) => <Field key={key}>
            <FieldLabel htmlFor={`${id}-${key}`}>{label}{['date_of_birth', 'first_name', 'last_name'].includes(key) ? ' *' : ''}</FieldLabel>
            <Input id={`${id}-${key}`} type={type} disabled={busy} value={values[key]}
              required={['date_of_birth', 'first_name', 'last_name'].includes(key)}
              max={type === 'date' ? `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}` : undefined}
              onChange={e => edit(key, e.target.value)} />
          </Field>)}
          <Field><FieldLabel>Sex (as recorded in the source)</FieldLabel>
            <ToggleGroup type="single" disabled={busy} aria-label="Sex" variant="outline" value={values.gender} onValueChange={v => edit('gender', v)}>
              <ToggleGroupItem value="F">Female</ToggleGroupItem><ToggleGroupItem value="M">Male</ToggleGroupItem><ToggleGroupItem value="O">Other</ToggleGroupItem>
            </ToggleGroup><FieldDescription>Leave unselected if unknown.</FieldDescription></Field>
          <Field><FieldLabel htmlFor={`${id}-mrn`}>Local medical record number</FieldLabel>
            <Input id={`${id}-mrn`} value={mrn} disabled={busy} placeholder="Generate automatically" onChange={e => { setMrn(e.target.value); setReviewed(false); }} />
            <FieldDescription>Leave blank to generate a unique local MRN.</FieldDescription></Field>
        </FieldGroup>
        {!!suggestion.matches.length && <Alert><AlertDescription className="flex flex-col gap-3">
          <p>Possible existing charts — review these before creating another patient. Matching keeps the existing chart details.</p>
          {suggestion.matches.map(patient => <div key={patient.id} className="flex flex-wrap items-center justify-between gap-2">
            <span>{patient.metadata_?.first_name} {patient.metadata_?.last_name} · DOB {patient.metadata_?.date_of_birth || 'unknown'} · MRN {patient.medical_record_number}</span>
            <Button type="button" variant="outline" disabled={busy || !reviewed} onClick={() => void finish(patient.id)}>Match this patient</Button>
          </div>)}
          <Field orientation="horizontal"><Checkbox id={`${id}-similar`} disabled={busy} checked={allowSimilar} onCheckedChange={v => setAllowSimilar(v === true)} />
            <FieldLabel htmlFor={`${id}-similar`}>These are different people; create a new patient.</FieldLabel></Field>
        </AlertDescription></Alert>}
        <Field orientation="horizontal"><Checkbox id={`${id}-reviewed`} checked={reviewed} disabled={busy || loading || suggestion.processing} onCheckedChange={v => setReviewed(v === true)} />
          <FieldLabel htmlFor={`${id}-reviewed`}>I reviewed the source and confirmed the patient identity and details.</FieldLabel></Field>
        <div className="flex justify-end gap-2">
          <Button type="button" disabled={busy} variant="outline" onClick={close}>Cancel</Button>
          <Button type="submit" disabled={busy || loading || !reviewed || (!!suggestion.matches.length && !allowSimilar)}>
            {busy && <Loader2 className="animate-spin" data-icon="inline-start" />}Create patient and link
          </Button>
        </div>
      </form>}
    </DialogContent>
  </Dialog>;
}
